<template>
  <v-row
    v-if="tile"
    no-gutters
  >
    <v-col>
      <v-row no-gutters>
        <v-card
          class="fp-file--card elevation-0 cursor-pointer!"
          :class="{
            'fp-file--card-dragging': isFileOver,
            'card-error': hasError,
            'file-uploaded': isValueExist,
          }"
          @drop.prevent="fileHandler($event)"
          @click="openFileExplorer"
          @dragover.prevent="isFileOver = true"
          @dragleave.prevent="isFileOver = false"
        >
          <input
            type="file"
            class="d-none"
            ref="fileInput"
            :multiple="multiple"
            v-show="false"
            :value="inputValue"
            :accept="accept"
            @input="fileHandler($event)"
          />
          <slot>
            <v-card-title class="px-3 pt-3 pb-0 text-center subtitle-1 font-weight-medium fp-file--card-text">
              <template>
                <v-icon
                  v-if="isValueExist"
                  color="success"
                  >mdi-check</v-icon
                >
                <v-icon v-else>mdi-paperclip</v-icon>
              </template>
              <span class="ml-1">{{ label }}</span>
            </v-card-title>
            <v-card-text class="pa-4 body-2 d-block">
              <v-layout
                column
                v-if="Boolean(temporaryValue && (!Array.isArray(temporaryValue) || temporaryValue.length))"
              >
                <template v-if="Array.isArray(temporaryValue)">
                  <v-flex
                    d-flex
                    v-for="(item, index) in temporaryValue"
                    :key="index"
                  >
                    <span
                      class="font-weight-medium"
                      :class="{ 'text-truncate': truncateText }"
                    >
                      {{ getItemName(item) }}
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn
                      @click="removeFile(index)"
                      small
                      :ripple="false"
                      icon
                    >
                      <v-icon color="error">mdi-delete</v-icon>
                    </v-btn>
                  </v-flex>
                </template>
                <v-flex
                  d-flex
                  v-else
                >
                  <span
                    class="font-weight-medium"
                    :class="{ 'text-truncate': truncateText }"
                  >
                    {{ getItemName(temporaryValue) }}
                  </span>
                  <v-spacer></v-spacer>
                  <v-btn
                    @click.native.stop="removeFile"
                    small
                    :ripple="false"
                    icon
                  >
                    <v-icon color="error">mdi-delete</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
              <span
                v-else
                class="subtitle-1 text-center d-block fp-file--card-text text--secondary"
              >
                {{ $i18n.fpFile.DROP_HERE }}
              </span>
            </v-card-text>
            <v-card-actions class="px-4 pb-3 pt-0">
              <v-btn
                :color="hasError ? 'error' : isValueExist ? '' : 'primary'"
                depressed
                :outlined="isValueExist"
                class="text-none default-button-class"
                :class="{
                  'dotted-button': isValueExist,
                }"
                tag="label"
              >
                {{
                  isValueExist
                    ? multiple
                      ? $i18n.fpFile.PICK_ANOTHER
                      : $i18n.fpFile.PICK_AGAIN
                    : $i18n.fpFile.PICK_FILE
                }}
              </v-btn>
            </v-card-actions>
          </slot>
        </v-card>
      </v-row>
      <v-row
        no-gutters
        v-if="hasError"
      >
        <v-col>
          <span class="error--text caption">{{ $i18n.fpFile.FILE_REQUIRED }}</span>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <v-flex
    v-else
    class="fp-file"
  >
    <slot name="left"></slot>
    <fp-btn
      :icon="icon"
      :error="hasError"
      :primary="primary"
      :dashed="dashed"
      @click="openFileExplorer()"
      tag="label"
      class="default-button-class"
      :class="typeof customClass === 'string' ? [customClass] : customClass"
    >
      <v-icon v-if="icon">mdi-paperclip</v-icon>
      {{ label }}
      <input
        type="file"
        class="d-none"
        :ref="FILE_INPUT_REF"
        :multiple="multiple"
        :accept="accept"
        @change="fileHandler($event)"
      />
    </fp-btn>
    <span
      v-if="showFileName"
      class="file-name"
      :class="{ 'text-truncate': truncateText }"
    >
      {{ getItemName(value) }}
    </span>
    <slot name="right"></slot>
  </v-flex>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue, Inject } from 'vue-property-decorator';
import { VBtn, VCard, VCardActions, VCardText, VCardTitle, VFlex, VIcon, VLayout, VSpacer } from 'vuetify/lib';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';

export interface IFpFile {
  file: File | null;
  fileUrl?: string;
}

@Component({
  components: { VFlex, VIcon, VCard, VCardText, VCardActions, VSpacer, VCardTitle, VBtn, VLayout, },
})
export default class FpFile extends Vue {
  @Watch('value') handler() {
    this.removeAllFiles();
  }

  public I18NGetter = I18NGetter;

  @Prop()
  public readonly value!: Array<any> | null;

  @Prop({default: () => I18NGetter().fpFile.PICK, type: String,}) public label!: string;
  @Prop({ default: 'image/jpeg, image/png, application/pdf, image/webp, image/svg+xml', type: String,}) public accept!: string;
  @Prop({ default: false, type: Boolean,}) public required!: boolean;
  @Prop({ default: false, type: Boolean,}) public multiple!: boolean;
  @Prop({ default: false, type: Boolean,}) public tile!: boolean;
  @Prop({ default: false, type: Boolean,}) public icon!: boolean;
  @Prop({ default: false, type: Boolean,}) public primary!: boolean;
  @Prop({ default: false, type: Boolean,}) public dashed!: boolean;
  @Prop({ default: false, type: Boolean,}) public getUrl!: boolean;
  @Prop({ default: '', type: String,}) public customClass!: string | Array<any>;
  @Prop({ default: true, type: Boolean,}) public truncateText!: boolean;
  @Prop({ default: false, type: Boolean,}) public showFileName!: boolean;

  @Inject({ from: 'form', default: () => null,})
  public form?: any;

  public temporaryValue: IFpFile | Array<IFpFile | File> | File | null = this.multiple ? [] : null;
  public isFileOver: boolean = false;
  public inputValue: Nullable<File> = null;
  public readonly FILE_INPUT_REF: string = 'fileInput';
  public hasError = false;

  public fileHandler(e) {
    const droppedFiles = e.dataTransfer || e.target;
    if (!droppedFiles.files) {
      return;
    }
    [...droppedFiles.files,]
      .filter((file) => {
        const isValid = this.accept.includes(file.type);
        if (!isValid) {
          this.$snackbarService.openErrorSnackbar(I18NGetter().fpFile.NOT_ALLOWED_EXTENSIONS);
        }
        return isValid;
      })
      .forEach((file) => {
        this.emitFile(file);
      });
    this.isFileOver = false;
  }

  public openFileExplorer() {
    const $ref: any = this.$refs[this.FILE_INPUT_REF];
    if ($ref && $ref.click) {
      $ref.click();
    }
  }

  getItemName(item: File | IFpFile | Array<any> | null): string {
    if (!item) return '';

    if (Array.isArray(item)) {
      return this.getItemName(item[0] || null);
    }

    if (this.getUrl) {
      if ('file' in item && item.file) {
        return item.file.name;
      }
      return '';
    } else {
      if ('name' in item) {
        return item.name;
      }
      return '';
    }
  }

  validate(): boolean {
    this.hasError = this.required && !this.value;
    return !this.hasError;
  }

  resetValidation(): void {
    this.hasError = false;
  }

  created() {
    if (this.form) {
      this.form.register(this);
    }
  }

  public destroyed() {
    if (this.form) {
      this.form.unregister(this);
    }
  }

  public async emitFile(file: File) {
    const url = URL.createObjectURL(new Blob([file,]));
    const fileFormat = this.getUrl ? { file: file, fileUrl: url,} : file;
    if (this.multiple && Array.isArray(this.temporaryValue)) {
      this.temporaryValue.push(fileFormat);
    } else {
      this.temporaryValue = this.getUrl ? { file: file, fileUrl: url,} : file;
    }
    this.inputValue = null;
    this.$emit('input', this.temporaryValue);
    this.resetValidation();
  }

  public removeFile(index?: number) {
    const fileInput = this.$refs[this.FILE_INPUT_REF] as any;
    if (this.multiple && index && Array.isArray(this.temporaryValue)) {
      this.temporaryValue.splice(index, 1);
    } else {
      this.temporaryValue = null;
    }
    fileInput.value = '';
    this.$emit('input', null);
  }

  public removeAllFiles() {
    if (!this.value || (Array.isArray(this.value) && !this.value.length)) {
      const fileInput = this.$refs[this.FILE_INPUT_REF] as any;
      fileInput.value = '';
      if (this.multiple && Array.isArray(this.temporaryValue)) {
        this.temporaryValue = [];
      } else {
        this.temporaryValue = null;
      }
    }
  }

  get isValueExist(): boolean {
    return Boolean(
      (this.temporaryValue && !Array.isArray(this.temporaryValue)) ||
        (Array.isArray(this.temporaryValue) && this.temporaryValue.length),
    );
  }
}
</script>

<style scoped lang="scss">
.fp-file {
  display: flex;
  align-items: center;
  .file-name {
    line-height: initial !important;
  }
  &--card {
    width: 100%;
    background-color: transparent !important;
    border: 2px dashed onCoreAlpha(0.25) !important;
    border-radius: 4px !important;
    -webkit-border-radius: 4px !important;
    -moz-border-radius: 4px !important;
    transition: border 0.3s !important;
    &-text {
      line-height: 22px !important;
    }
    &-dragging {
      border: 2px dashed var(--token-primary-base) !important;
    }
  }
}

.card-error {
  border: 2px dashed var(--token-error-border) !important;
}
.default-button-class {
  cursor: pointer;
  &.dotted-button {
    border: 2px dashed onCoreAlpha(0.25) !important;
  }
}
.file-uploaded {
  background-color: white !important;
  border: 1px solid onCoreAlpha(0.15) !important;
}
</style>
